import {
  format as doFormat,
  formatDistanceToNowStrict,
  isValid,
  isPast,
  formatDistanceStrict,
  intervalToDuration,
  differenceInSeconds,
  differenceInHours,
  parseISO,
} from "date-fns";
import { format as formatTz, zonedTimeToUtc } from "date-fns-tz";
import enAU from "date-fns/locale/en-AU";
import type { Maybe, Nullable, NullableMaybe } from "@/types/utilities";

/**
 * @description Return a description of the duration between now and datems, using our abbreviated unit format. E.g. 1D, 3H, 3Mo
 * @param datems the date
 */
export function formatDistanceToNowAbbr(datems, skipPrefix = false) {
  if (!datems) {
    return "-";
  }

  //"inspired" by https://github.com/date-fns/date-fns/issues/1706#issuecomment-836601089
  const duration = differenceInSeconds(datems, new Date());

  const prefix = duration <= 0 && !skipPrefix ? "-" : "";

  if (duration <= 60 && duration >= -60) {
    // for everything below minute or above -minute show seconds only
    return `${duration}S`;
  }

  if (
    (duration < 5 * 60 && duration > 1 * 60) ||
    (duration > -5 * 60 && duration <= -1 * 60)
  ) {
    const interval = intervalToDuration({ start: new Date(), end: datems });
    return `${prefix}${interval.minutes}M ${interval.seconds}S`;
  }

  return (
    prefix +
    formatDistanceToNowStrict(datems, {
      locale: {
        ...enAU,
        formatDistance: (token, count) => {
          return formatDistanceAbbr[token].replace("{{count}}", count);
        },
      },
    })
  );
}

/**
 * @description Return a description of the duration between date and given date, using our abbreviated unit format. E.g. 1D, 3H, 3Mo
 * @param date the date
 */
export function formatDistanceToDateFromNowAbbr(date, skipPrefix = false) {
  if (!date) {
    return "-";
  }

  const prefix = isPast(date) && !skipPrefix ? "-" : "";

  return (
    prefix +
    formatDistanceStrict(new Date(), date, {
      roundingMethod: "floor",
      locale: {
        ...enAU,
        formatDistance: (token, count) =>
          formatDistanceAbbr[token].replace("{{count}}", count),
      },
    })
  );
}

const formatDistanceAbbr = {
  lessThanXSeconds: "{{count}}S",
  xSeconds: "{{count}}S",
  halfAMinute: "30S",
  lessThanXMinutes: "{{count}}M",
  xMinutes: "{{count}}M",
  aboutXHours: "{{count}}H",
  xHours: "{{count}}H",
  xDays: "{{count}}D",
  aboutXWeeks: "{{count}}W",
  xWeeks: "{{count}}W",
  aboutXMonths: "{{count}}Mo",
  xMonths: "{{count}}Mo",
  aboutXYears: "{{count}}Y",
  xYears: "{{count}}Y",
  overXYears: "{{count}}Y",
  almostXYears: "{{count}}Y",
};

/**
 * @name formatDateTime
 * @description Returns a string representation of a datetime for human consumption
 * @param {String} dateTime - The ISD-8601 string representation of date to format
 * @param format
 */
export function formatDateTime(
  dateTime: Date | string,
  format = "do MMM, yy @ HH:mm:ss",
) {
  return doDateFormatting(dateTime, format);
}

export function formatDateTimeWithTz(
  dateTime: Date | string,
  format = "do MMM, yy @ HH:mm:ss z",
) {
  const local = parseDate(dateTime);

  if (isValid(local) === false) {
    return null;
  }

  const timeZone =
    Intl.DateTimeFormat()?.resolvedOptions()?.timeZone || "Australia/Brisbane";

  return formatTz(local, format, {
    timeZone,
    locale: enAU,
  });
}

/**
 * @description Parses a date string known to be in AEST into a Date object in UTC
 * @param aestDateString The date string, assumed to be in AEST (e.g. "2025-07-26 15:30")
 */
export function convertAESTToDateObject(aestDateString: string): Date | null {
  if (!aestDateString) {
    return null;
  }

  const dateObjectInUtc = zonedTimeToUtc(aestDateString, "Australia/Brisbane");

  return isValid(dateObjectInUtc) ? dateObjectInUtc : null;
}

/**
 * @name formatDate
 * @description Returns a string representation of a date for human consumption
 * @param {String} dateTime - The ISD-8601 string representation of date to format
 */
export function formatDate(dateTime: Date | string) {
  return doDateFormatting(dateTime, "do MMM, yy");
}

function doDateFormatting(dateTime: Date | string, format: string) {
  const local = parseDate(dateTime);

  if (isValid(local) === false) {
    return null;
  }
  return doFormat(local, format);
}

/**
 * Returns an array of strings with each year dating back to the one provided
 */
export const getYearsBackTo = (
  endYear: number,
  startYear?: number,
): string[] => {
  if (!endYear) return [];

  if (startYear < endYear) return [String(startYear)];

  const currentYear = startYear ? startYear : new Date().getFullYear();

  const years = Array.from({ length: currentYear - endYear + 1 }).map(
    (_val, index) => String(currentYear - index),
  );

  return years;
};

/**
 * Converts seconds to miliseconds
 */
export const msFromSeconds = (seconds: number) => seconds * 1000;

/**
 * Converts timestamp seconds to a Date
 */
export const dateFromSeconds = (seconds: number) =>
  new Date(msFromSeconds(seconds));

export const getHoursUntil = (date: Date | string) => {
  const parsedDate = parseDate(date);

  if (!isValid(parsedDate) || parsedDate === null) {
    return Number.POSITIVE_INFINITY;
  }

  return differenceInHours(parsedDate, new Date());
};

export function parseDate(date: NullableMaybe<string | Date>): Nullable<Date> {
  if (!date) {
    return null;
  }

  if (typeof date === "string") {
    const hasTimezone = Boolean(date.match(/(Z|[\+\-][\d:]*)$/));
    if (!hasTimezone) {
      date += "Z";
    }
    date = parseISO(date as string);
  }

  return isValid(date) ? date : null;
}
